import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  QuestionAnswer,
  Quiz,
  People,
  AttachMoney,
  TrendingUp,
  CheckCircle,
  PendingActions,
} from '@mui/icons-material';
import { api } from '../services/api';

interface DashboardData {
  questions: {
    total: number;
    by_status: Array<{ status: string; count: number }>;
  };
  exams: {
    total: number;
    total_attempts: number;
  };
  users: {
    total_parents: number;
    total_students: number;
  };
  revenue: {
    total: number;
    this_month: number;
    today: number;
    pending: {
      amount: number;
      count: number;
    };
  };
  admin_users: number;
  recent_activity: Array<{ date: string; registrations: number }>;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            borderRadius: 2,
            p: 2,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.analytics.getDashboard();
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="warning">No data available</Alert>;
  }

  const approvedQuestions = data.questions.by_status.find((s) => s.status === 'approved')?.count || 0;
  const pendingQuestions = data.questions.by_status.find((s) => s.status === 'pending_review')?.count || 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Dashboard Overview
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Welcome back! Here's what's happening with your platform today.
      </Typography>

      <Grid container spacing={3}>
        {/* Questions Stats */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Questions"
            value={data.questions.total}
            icon={<QuestionAnswer fontSize="large" />}
            color="primary"
            subtitle={`${approvedQuestions} approved`}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Review"
            value={pendingQuestions}
            icon={<PendingActions fontSize="large" />}
            color="warning"
            subtitle="Needs verification"
          />
        </Grid>

        {/* Exams Stats */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Exams"
            value={data.exams.total}
            icon={<Quiz fontSize="large" />}
            color="secondary"
            subtitle={`${data.exams.total_attempts} attempts`}
          />
        </Grid>

        {/* Users Stats */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={data.users.total_parents + data.users.total_students}
            icon={<People fontSize="large" />}
            color="info"
            subtitle={`${data.users.total_parents} parents, ${data.users.total_students} students`}
          />
        </Grid>

        {/* Revenue Stats */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Revenue"
            value={`₹${data.revenue.total.toLocaleString()}`}
            icon={<AttachMoney fontSize="large" />}
            color="success"
            subtitle="All time"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="This Month"
            value={`₹${data.revenue.this_month.toLocaleString()}`}
            icon={<TrendingUp fontSize="large" />}
            color="success"
            subtitle="Current month revenue"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Today's Revenue"
            value={`₹${data.revenue.today.toLocaleString()}`}
            icon={<AttachMoney fontSize="large" />}
            color="success"
            subtitle="Today's earnings"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Payments"
            value={`₹${data.revenue.pending.amount.toLocaleString()}`}
            icon={<PendingActions fontSize="large" />}
            color="warning"
            subtitle={`${data.revenue.pending.count} transactions`}
          />
        </Grid>

        {/* Question Status Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Question Status Breakdown
            </Typography>
            <Box sx={{ mt: 2 }}>
              {data.questions.by_status.map((statusData) => (
                <Box
                  key={statusData.status}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle
                      fontSize="small"
                      color={
                        statusData.status === 'approved'
                          ? 'success'
                          : statusData.status === 'pending_review'
                          ? 'warning'
                          : statusData.status === 'rejected'
                          ? 'error'
                          : 'disabled'
                      }
                    />
                    <Typography sx={{ textTransform: 'capitalize' }}>
                      {statusData.status.replace('_', ' ')}
                    </Typography>
                  </Box>
                  <Typography fontWeight="bold">{statusData.count}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Recent Registrations (Last 7 Days)
            </Typography>
            <Box sx={{ mt: 2 }}>
              {data.recent_activity.length > 0 ? (
                data.recent_activity.slice(0, 7).map((activity) => (
                  <Box
                    key={activity.date}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2">
                      {new Date(activity.date).toLocaleDateString('en-IN', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </Typography>
                    <Typography fontWeight="bold">{activity.registrations} new users</Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent registrations
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
